/**
 * "Generate Abby's voice" for the Abby welcome tab: the engine of
 * lib/voice-generation.ts, connected to the two voice routes (api.ts) and to
 * React state. The rules are in the engine; this file only wires them.
 *
 * Safe to leave: unmount resets the engine, so a late answer changes nothing.
 * `onReady` receives exactly what assemble returned (Publish sends it, R19).
 * Proved through the real hook in tests/unit/admin/app-experience-voice-hook.test.ts.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { assembleWelcomeVoice, synthesizeWelcomeParagraph } from './api';
import type { ExperienceLocale, WelcomeVoiceLocale } from './lib/contract';
import { EMPTY_GENERATION, createVoiceGeneration, type GenerationSnapshot, type VoiceGeneration } from './lib/voice-generation';

export function useVoiceGeneration(apiUrl: string, onReady: (locale: ExperienceLocale, voice: WelcomeVoiceLocale) => void) {
  const [snapshot, setSnapshot] = useState<GenerationSnapshot>(EMPTY_GENERATION);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  const apiUrlRef = useRef(apiUrl);
  apiUrlRef.current = apiUrl;
  const alive = useRef(true);
  const engineRef = useRef<VoiceGeneration | null>(null);

  if (engineRef.current === null) {
    engineRef.current = createVoiceGeneration(
      {
        synthesize: (text, use) => synthesizeWelcomeParagraph(apiUrlRef.current, text, use),
        assemble: (locale, paragraphs) => assembleWelcomeVoice(apiUrlRef.current, locale, paragraphs),
      },
      {
        onUpdate: (s) => {
          if (alive.current) setSnapshot(s);
        },
        onReady: (locale, voice) => {
          if (alive.current) onReadyRef.current(locale, voice);
        },
      },
    );
  }

  useEffect(() => {
    alive.current = true;
    const engine = engineRef.current;
    return () => {
      alive.current = false;
      engine?.reset();
    };
  }, []);

  const start = useCallback((input: Partial<Record<ExperienceLocale, readonly string[]>>) => engineRef.current!.start(input), []);
  const retry = useCallback((locale: ExperienceLocale, index: number) => engineRef.current!.retry(locale, index), []);
  const retryJoin = useCallback((locale: ExperienceLocale) => engineRef.current!.retryJoin(locale), []);
  const reset = useCallback(() => engineRef.current!.reset(), []);

  return { snapshot, running: snapshot.running, start, retry, retryJoin, reset };
}

export type VoiceGenerationControl = ReturnType<typeof useVoiceGeneration>;
